import { Injectable } from '@angular/core';
import { UtilsService } from '../../services/utils/utils.service';
import { translate } from '@ngneat/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { createEntityAdapter } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import { from, of } from 'rxjs';
import { catchError, delay, map, mergeMap, withLatestFrom } from 'rxjs/operators';

import { AppIntegration } from '../../interface/appintegration.interface';
import { Integration } from '../../interface/integration.interface';
import { ApiService } from '../../services/api/api.service';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import { GetAssetWithLatestVersion, GetDynamicParamsKeys, SetAuthorizationSystemOptions, SetDynamicParamsKeys } from '../actions';
import * as actions from '../actions/integrations.actions';
import { SetCurrentOrganization } from '../actions/session.actions';
import { AlertError, AlertSuccess } from '../actions/shared.actions';
import { AppIntegrationState, IntegrationOrganizationsState, IntergrationsConfigState } from '../interface/integration-state.interface';
import {
    authSystemFieldsOrgMap$,
    getAppIntegration$,
    getAppIntegrationConfigs$,
    getAssetsWithLatestVersion$,
    getAuthorizationSystemOptions$,
    getConnectorRequiredFieldsMap$,
    getConnectors$,
    getCurrentOrganizationId$,
    getIntegrationActiveTab$,
    getIntegrationConfigEntity$,
    getIntegrationConfigurations$,
    getIntegrationOrganization$,
    getIntegrationsOrganizationEntity$,
    getIntegrationsSupportedTypes$,
    isProviderOrganizationAndAdmin$,
} from '../selectors';
import { integrationAdapters } from '../states';

@Injectable()
export class IntegrationEffects {
    constructor(private actions$: Actions, private _api: ApiService, private store$: Store) {}

    setCurrentOrganization$ = createEffect(() => {
        return this.actions$.pipe(
            delay(10),
            ofType(SetCurrentOrganization),
            withLatestFrom(this.store$.select(getIntegrationsOrganizationEntity$)),
            mergeMap(([action, organizationEntity]) => {
                if (!organizationEntity) {
                    const configsAdapter = createEntityAdapter<IntergrationsConfigState>();
                    const appDetailsAdapter = createEntityAdapter<AppIntegrationState>();
                    const organizationObject: IntegrationOrganizationsState = {
                        id: action.organization.id,
                        configs: configsAdapter.getInitialState(),
                        appIntegrations: appDetailsAdapter.getInitialState(),
                    };
                    integrationAdapters[action.organization.id] = configsAdapter;
                    return of(
                        actions.SetIntegrationOrganizationEntity({
                            organizationEntity: organizationObject,
                            organizationsEntityAdapter: integrationAdapters.organizationsAdapter,
                        })
                    );
                }
                return [];
            })
        );
    });

    getSupportedIntegrationConfigurations$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetIntegrationsConfig),
            delay(100),
            withLatestFrom(
                this.store$.select(getAppIntegrationConfigs$),
                this.store$.select(getAuthorizationSystemOptions$),
                this.store$.select(getIntegrationsSupportedTypes$),
                this.store$.select(isProviderOrganizationAndAdmin$)
            ),
            mergeMap(([_action, supportedMethods, existingSystems, existingSupportedTypes, isProviderAdmin]) => {
                if (
                    !_action.forceFetch &&
                    supportedMethods !== undefined &&
                    existingSupportedTypes?.supportedAuthenticationTypes?.length > 0 &&
                    existingSupportedTypes?.supportedGrantTypes?.length > 0 &&
                    existingSupportedTypes?.clientAuthentications?.length > 0 &&
                    (existingSystems?.RESTAPI?.length > 0 || existingSystems?.EMAIL?.length > 0)
                ) {
                    return [];
                }
                return from(this._api.integrations.getIntegrationsConfig()).pipe(
                    map(
                        (res: {
                            supportedMethods: { inbound: string[]; outbound: string[]; masterinbound: string[] };
                            oAuth2CodeFlowSystems: { [property: string]: string };
                            emailConfigs: { [property: string]: string };
                            supportedAuthenticationTypes: { [property: string]: string };
                            supportedGrantTypes: { [property: string]: string };
                            connectorSourceSystems: { [property: string]: string };
                            ftpProtocols: string[];
                            customAuthSchema: {
                                accessTokenURI: string;
                                requestPayload: string;
                                headers: string;
                                responseSchema: {
                                    tokenKey: string;
                                    tokenTypeKey: string;
                                    expiryKey: string;
                                };
                                defaultExpiryTime: string;
                                authorizationKey: string;
                                method: string;
                            };
                            emailFilterKeys: string[];
                            paginationSchema: {};
                            masterSchedulerEnabled: boolean;
                            useMasterDisabled: boolean;
                            clientAuthentications: { [property: string]: string };
                            RestApiResponseType: string[];
                            payloadType?: any[];
                            formDataTypes?: string[];
                        }) => {
                            this.store$.dispatch(
                                SetAuthorizationSystemOptions({
                                    RESTAPI: Object.keys(res?.oAuth2CodeFlowSystems).reduce((options, key) => {
                                        options.push({
                                            id: key,
                                            displayName: res?.oAuth2CodeFlowSystems[key],
                                        });
                                        return options;
                                    }, []),
                                    EMAIL: Object.keys(res?.emailConfigs).reduce((options, key) => {
                                        options.push({
                                            id: key,
                                            displayName: res?.emailConfigs[key],
                                        });
                                        return options;
                                    }, []),
                                })
                            );
                            this.store$.dispatch(
                                actions.SetIntegrationsSupportedTypes({
                                    supportedAuthenticationTypes: Object.keys(res?.supportedAuthenticationTypes).reduce((options, key) => {
                                        options.push({
                                            id: key,
                                            displayName: res?.supportedAuthenticationTypes[key],
                                            value: key,
                                        });
                                        return options;
                                    }, []),
                                    supportedGrantTypes: Object.keys(res?.supportedGrantTypes).reduce((options, key) => {
                                        options.push({
                                            id: key,
                                            displayName: res?.supportedGrantTypes[key],
                                            value: key,
                                        });
                                        return options;
                                    }, []),
                                    ftpProtocols: res?.ftpProtocols?.reduce((options, key) => {
                                        options.push({
                                            id: key,
                                            displayName: key,
                                            value: key,
                                        });
                                        return options;
                                    }, []),
                                    customAuthSchema: res?.customAuthSchema,
                                    paginationSchema: res?.paginationSchema,
                                    emailFilterKeys: res?.emailFilterKeys?.reduce((options, key) => {
                                        options.push({
                                            id: key,
                                            displayName: UtilsService.convertEnumsToNormalWords(key),
                                            value: key,
                                        });
                                        return options;
                                    }, []),
                                    useMasterDisabled: res?.useMasterDisabled,
                                    masterSchedulerEnabled: res?.masterSchedulerEnabled,
                                    connectorSourceSystems: Object.keys(res?.connectorSourceSystems).reduce((options, key) => {
                                        options.push({
                                            id: key,
                                            displayName: res?.connectorSourceSystems[key],
                                            value: key,
                                            actionsRequired: isProviderAdmin,
                                        });
                                        return options;
                                    }, []),
                                    clientAuthentications: (res?.clientAuthentications as any)?.map((item: string) => {
                                        return {
                                            id: item,
                                            displayName: item,
                                            value: item,
                                        };
                                    }),
                                    restApiResponseType: (res?.RestApiResponseType as any)?.map((item: string) => {
                                        return {
                                            id: item,
                                            displayName: item,
                                            value: item,
                                        };
                                    }),
                                    payloadTypes: res?.payloadType?.map((item: any) => ({
                                        id: item.type,
                                        displayName: UtilsService.convertEnumsToNormalWords(item.type),
                                        value: item.type,
                                        name: item.type,
                                        applicableFields: Object.entries(item)
                                            .filter(([key, val]) => key !== 'type' && val === true)
                                            .map(([key]) => key),
                                    })),
                                    formDataTypes: res?.formDataTypes?.map((item: string) => ({
                                        id: item,
                                        displayName: UtilsService.convertEnumsToNormalWords(item),
                                        value: item,
                                        name: item,
                                    })),
                                })
                            );
                            const formatMethods = (methods: string[]) =>
                                methods.map(method => ({
                                    id: method.toLowerCase(),
                                    displayName: method,
                                    value: method,
                                })) || [];
                            return actions.SetAppIntegrationConfigs({
                                appIntegrationConfigs: {
                                    inbound: formatMethods(res?.supportedMethods?.inbound || []),
                                    outbound: formatMethods(res?.supportedMethods?.outbound || []),
                                    masterinbound: formatMethods(res?.supportedMethods?.masterinbound || []),
                                },
                            });
                        }
                    ),
                    catchError(e => of(AlertError({ message: e?.msg || e })))
                );
            })
        );
    });

    setActiveIntegrationTab$ = createEffect(() => {
        return this.actions$.pipe(
            delay(10),
            ofType(actions.SetActiveIntegrationTab),
            withLatestFrom(this.store$.select(getIntegrationsOrganizationEntity$), this.store$.select(getIntegrationConfigEntity$)),
            mergeMap(([{ tab }, organizationEntity, configsEntity]) => {
                if (!configsEntity) {
                    const integrationsAdapter = createEntityAdapter<Integration>();
                    integrationAdapters[`${organizationEntity?.id}|${tab}`] = integrationsAdapter;
                    const configEntity = {
                        id: tab,
                        integrations: integrationsAdapter.getInitialState(),
                    };
                    return of(
                        actions.SetIntegrationConfigEntity({
                            organizationEntity: organizationEntity,
                            organizationAdapter: integrationAdapters.organizationsAdapter,
                            configsEntity: configEntity,
                            configsEntityAdapter: integrationAdapters[organizationEntity?.id],
                        })
                    );
                }
                return [];
            })
        );
    });

    getIntegrationRecords$ = createEffect(() => {
        return this.actions$.pipe(
            delay(10),
            ofType(actions.GetIntegrationRecords),
            withLatestFrom(
                this.store$.select(getIntegrationActiveTab$),
                this.store$.select(getIntegrationOrganization$),
                this.store$.select(getIntegrationConfigurations$)
            ),
            mergeMap(([action, config, currentOrganizationId, integrations]) => {
                if (!action.forceFetch && integrations?.length > 0) {
                    return [];
                }
                return from(
                    this._api.integrations.getIntegrationConfigurations({
                        config: action.config || config,
                        dontFetchUsingMasters: action.dontFetchUsingMasters,
                        isMaster: action.isMaster,
                    })
                ).pipe(
                    map(response =>
                        actions.SetIntegrationRecords({
                            organizationAdapter: integrationAdapters.organizationsAdapter,
                            configsAdapter: integrationAdapters[currentOrganizationId],
                            integrationsAdapter: integrationAdapters[`${currentOrganizationId}|${config}`],
                            records: response,
                        })
                    ),
                    catchError(errorResponse => of(AlertError({ message: errorResponse })))
                );
            })
        );
    });

    testIntegrationConfiguration$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.TestIntegrationConfiguration),
            mergeMap(action => {
                return from(this._api.integrations.testConnection(action.payload)).pipe(
                    map(response => {
                        if (response?.connected) {
                            return AlertSuccess({
                                message: response?.msg || translate('Successfully tested the connection'),
                            });
                        } else {
                            return AlertError({
                                message: response?.msg || translate('Testing connection failed'),
                            });
                        }
                    }),
                    catchError(errorResponse => of(AlertError({ message: errorResponse })))
                );
            })
        );
    });

    saveIntegrationConfiguration$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.SaveIntegrationConfiguration),
            withLatestFrom(this.store$.select(getIntegrationOrganization$), this.store$.select(getIntegrationActiveTab$)),
            mergeMap(([action, organizationId, config]) => {
                return from(this._api.integrations.saveIntegrationRecord(action.payload)).pipe(
                    map(response => {
                        this.store$.dispatch(
                            AlertSuccess({
                                message: translate('Configuration saved successfully'),
                            })
                        );
                        if (!action.payload.id) {
                            return actions.PushIntegrationConfiguration({
                                organizationAdapter: integrationAdapters.organizationsAdapter,
                                configsAdapter: integrationAdapters[organizationId],
                                integrationsAdapter: integrationAdapters[`${organizationId}|${config}`],
                                record: response,
                            });
                        }
                        return actions.UpdateIntegrationConfigurationChange({
                            organizationAdapter: integrationAdapters.organizationsAdapter,
                            configsAdapter: integrationAdapters[organizationId],
                            integrationsAdapter: integrationAdapters[`${organizationId}|${config}`],
                            record: response,
                        });
                    }),
                    catchError(errorResponse => of(AlertError({ message: errorResponse })))
                );
            })
        );
    });

    validateIntegrationConfiguration$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.ValidateIntegrationConfiguration),
            withLatestFrom(this.store$.select(getIntegrationOrganization$), this.store$.select(getIntegrationActiveTab$)),
            mergeMap(([action, organizationId, config]) => {
                return from(this._api.integrations.validateIntegrationRecord(action.payload)).pipe(
                    map((response: any) => {
                        if (response?.config) {
                            if (!action.payload.id) {
                                this.store$.dispatch(
                                    actions.PushIntegrationConfiguration({
                                        organizationAdapter: integrationAdapters.organizationsAdapter,
                                        configsAdapter: integrationAdapters[organizationId],
                                        integrationsAdapter: integrationAdapters[`${organizationId}|${config}`],
                                        record: response.config,
                                    })
                                );
                            }
                            this.store$.dispatch(
                                actions.UpdateIntegrationConfigurationChange({
                                    organizationAdapter: integrationAdapters.organizationsAdapter,
                                    configsAdapter: integrationAdapters[organizationId],
                                    integrationsAdapter: integrationAdapters[`${organizationId}|${config}`],
                                    record: response.config,
                                })
                            );
                        }
                        if (response?.authorizationURI?.length > 0) {
                            return actions.ValidateIntegrationConfigurationSuccess({ redirectURL: response.authorizationURI });
                        }
                        return AlertSuccess({
                            message: translate(`Authentication successful.`),
                        });
                    }),
                    catchError(errorResponse => of(AlertError({ message: errorResponse })))
                );
            })
        );
    });

    toggleIntegrationConfiguration$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.ToggleIntegrationConfiguration),
            withLatestFrom(this.store$.select(getIntegrationOrganization$), this.store$.select(getIntegrationActiveTab$)),
            mergeMap(([action, organizationId, config]) => {
                return from(this._api.integrations.saveIntegrationRecord(action.payload)).pipe(
                    map(response => {
                        this.store$.dispatch(
                            AlertSuccess({
                                message: translate(`configuration ${action.payload.active ? 'activated' : 'deactivated'}`),
                            })
                        );
                        return actions.UpdateIntegrationConfigurationChange({
                            organizationAdapter: integrationAdapters.organizationsAdapter,
                            configsAdapter: integrationAdapters[organizationId],
                            integrationsAdapter: integrationAdapters[`${organizationId}|${config}`],
                            record: response,
                        });
                    }),
                    catchError(errorResponse => of(AlertError({ message: errorResponse })))
                );
            })
        );
    });

    deleteIntegrationConfiguration$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.DeleteIntegrationConfiguration),
            withLatestFrom(this.store$.select(getIntegrationOrganization$), this.store$.select(getIntegrationActiveTab$)),
            mergeMap(([action, organizationId, config]) => {
                return this._api.integrations
                    .deleteIntegrationRecord({
                        name: action.record.name,
                        organizationId: action.record.organizationId || organizationId,
                        type: action.record.type.toLowerCase(),
                    })
                    .pipe(
                        map(response => {
                            this.store$.dispatch(
                                AlertSuccess({ message: translate(response?.msg || 'Successfully deleted the integration record') })
                            );
                            return actions.RemoveIntegrationConfiguration({
                                organizationAdapter: integrationAdapters.organizationsAdapter,
                                configsAdapter: integrationAdapters[organizationId],
                                integrationsAdapter: integrationAdapters[`${organizationId}|${config}`],
                                record: action.record,
                            });
                        }),
                        catchError(errorResponse =>
                            of(
                                AlertError({
                                    message: errorResponse || translate('Unable to delete integration record'),
                                })
                            )
                        )
                    );
            })
        );
    });

    getAppIntegrationDetails$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetAppIntegration),
            withLatestFrom(this.store$.select(getCurrentOrganizationId$), this.store$.select(getAppIntegration$)),
            mergeMap(([action, currentOrganizationId, appIntegration]) => {
                const appIntegrationAdapter = integrationAdapters[currentOrganizationId];
                const integrationsAdapter =
                    integrationAdapters[`${currentOrganizationId}|${appIntegration}`] ||
                    createEntityAdapter<AppIntegration>({
                        selectId: (a: AppIntegration) => {
                            return a.configName;
                        },
                    });
                if (!integrationAdapters[`${currentOrganizationId}|${appIntegration}`]) {
                    integrationAdapters[`${currentOrganizationId}|${appIntegration}`] = integrationsAdapter;
                }
                if (appIntegration?.length > 0) {
                    return of(
                        actions.SetAppIntegration({
                            organizationAdapter: integrationAdapters.organizationsAdapter,
                            appIntegrationAdapter,
                            integrationsAdapter,
                            serviceId: action?.serviceId,
                            appIntegrationDetails: CommonUtilsService.cloneObject(appIntegration),
                        })
                    );
                }
                return this._api.integrations
                    .getAppIntegrationsObservable({
                        integrationType: action?.integrationType,
                        serviceId: action?.serviceId,
                    })
                    .pipe(
                        map((res: AppIntegration) => {
                            return actions.SetAppIntegration({
                                organizationAdapter: integrationAdapters.organizationsAdapter,
                                appIntegrationAdapter,
                                integrationsAdapter,
                                serviceId: action?.serviceId,
                                appIntegrationDetails: res,
                            });
                        }),
                        catchError(e => of(AlertError({ message: e })))
                    );
            })
        );
    });

    deleteAppIntegrationDetails$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.DeleteAppIntegrationDetails),
            withLatestFrom(this.store$.select(getCurrentOrganizationId$), this.store$.select(getAppIntegration$)),
            mergeMap(([action, currentOrganizationId, appIntegration]) => {
                const appIntegrationAdapter = integrationAdapters[currentOrganizationId];
                const integrationsAdapter =
                    integrationAdapters[`${currentOrganizationId}|${appIntegration}`] ||
                    createEntityAdapter<AppIntegration>({
                        selectId: (a: AppIntegration) => {
                            return a.configName;
                        },
                    });
                if (!integrationAdapters[`${currentOrganizationId}|${appIntegration}`]) {
                    integrationAdapters[`${currentOrganizationId}|${appIntegration}`] = integrationsAdapter;
                }
                return of(
                    actions.DeleteAppIntegrationDetailsSuccess({
                        organizationAdapter: integrationAdapters.organizationsAdapter,
                        appIntegrationAdapter,
                        integrationsAdapter,
                        configName: action?.configName,
                        serviceId: action?.serviceId,
                    })
                );
            })
        );
    });

    public getDynamicParamsKeys$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GetDynamicParamsKeys),
            withLatestFrom(this.store$.select(getIntegrationsSupportedTypes$)),
            mergeMap(([_action, supportedTypes]) => {
                if (supportedTypes?.dynamicParamsKeys?.length > 0) {
                    return [];
                }
                return this._api.assets.getDynamicParamsKeys().pipe(
                    map(res =>
                        SetDynamicParamsKeys({
                            dynamicParamsKeys: res,
                        })
                    )
                );
            })
        )
    );

    public getConnectors$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.GetConnectors),
            withLatestFrom(this.store$.select(getConnectors$), this.store$.select(getCurrentOrganizationId$)),
            mergeMap(([_action, _connectors, organizationId]) => {
                return this._api.integrations.getConnectors().pipe(
                    map(res => {
                        return actions.SetConnectors({
                            organizationId,
                            connectors: res as any,
                        });
                    })
                );
            })
        )
    );

    public getConnector$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.GetConnector),
            withLatestFrom(this.store$.select(getCurrentOrganizationId$), this.store$.select(getConnectors$)),
            mergeMap(([action, organizationId, connectors]) => {
                const connector = connectors?.find(connector => connector.resourceName === action?.resourceName);
                if (connector) {
                    return [];
                }
                return this._api.integrations.getConnector(action?.resourceName).pipe(
                    map(res => {
                        return actions.SetConnector({
                            organizationId,
                            connector: res as any,
                        });
                    })
                );
            })
        )
    );

    public submitConnector = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.SubmitConnector),
            withLatestFrom(this.store$.select(getConnectors$), this.store$.select(getCurrentOrganizationId$)),
            mergeMap(([action, _connectors, organizationId]) =>
                this._api.integrations.saveConnectorConfiguration({ payload: action.connector as any, update: action.update }).pipe(
                    map(res => {
                        this.store$.dispatch(
                            AlertSuccess({ message: translate(`Connector ${action.update ? 'updated' : 'created'} successfully`) })
                        );
                        const response = CommonUtilsService.cloneObject(res);
                        return actions.SetConnector({
                            organizationId,
                            update: action.update,
                            connector: response as any,
                        });
                    }),
                    catchError(e => of(AlertError({ message: e?.msg || e })))
                )
            )
        )
    );

    public deleteConnector$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.DeleteConnector),
            withLatestFrom(this.store$.select(getConnectors$), this.store$.select(getCurrentOrganizationId$)),
            mergeMap(([action, _connectors, organizationId]) =>
                this._api.integrations.deleteConnector(action.resourceName).pipe(
                    map(() => {
                        this.store$.dispatch(AlertSuccess({ message: translate(`Connector deleted successfully`) }));
                        return actions.DeletedConnector({
                            organizationId,
                            resourceName: action.resourceName,
                        });
                    }),
                    catchError(e => of(AlertError({ message: e?.msg || e })))
                )
            )
        )
    );

    public getInternalConfig$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.GetInternalConfigs),
            withLatestFrom(this.store$.select(getCurrentOrganizationId$), this.store$.select(getAssetsWithLatestVersion$)),
            mergeMap(([action, organizationId, versionsMap]) => {
                if (action.forceFetch != true) {
                    return [];
                }
                if (!versionsMap?.[action.serviceId]?.metadataId) {
                    this.store$.dispatch(
                        GetAssetWithLatestVersion({
                            serviceId: action.serviceId,
                        })
                    );
                    setTimeout(() => {
                        this.store$.dispatch(actions.GetInternalConfigs(action));
                    }, 500);
                    return [];
                }
                return this._api.integrations
                    .getInternalConfigs({
                        serviceId: action.serviceId,
                        locationId: organizationId,
                    })
                    .pipe(
                        map((res: any) => {
                            return actions.SetInternalConfigs({
                                internalConfig: res?.reduce((options, key) => {
                                    options.push({
                                        id: key,
                                        displayName: key,
                                        value: key,
                                    });
                                    return options;
                                }, []),
                            });
                        })
                    );
            })
        )
    );

    public getCloneConnectorData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.GetCloneConnectorData),
            delay(100),
            mergeMap(action =>
                this._api.integrations.getCloneConnectorData(action.resourceName).pipe(
                    map((connector: any) => {
                        return actions.SetCloneConnectorData({ connector });
                    }),
                    catchError(e => of(AlertError({ message: e?.msg || e })))
                )
            )
        )
    );

    public cloneAndSubscribeConnector$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.CloneAndSubscribeConnector),
            mergeMap(action =>
                this._api.integrations
                    .cloneAndSubscribeConnector({
                        connector: action.connector,
                        organizationIds: action.orgIds,
                        requiredFields: action.requiredFields,
                    })
                    .pipe(
                        map((res: any) => {
                            this.store$.dispatch(
                                AlertSuccess({ message: translate('Clone and Subscription request placed successfully') })
                            );
                            return actions.RedirectToAuthorizationURI({
                                authorizationURI: res.authorizationURI,
                            });
                        }),
                        catchError(e => of(AlertError({ message: e?.msg || e })))
                    )
            )
        )
    );

    public getAuthSystemFields$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.GetAuthSystemFields),
            withLatestFrom(this.store$.select(getCurrentOrganizationId$), this.store$.select(authSystemFieldsOrgMap$)),
            mergeMap(([action, organizationId, authSystemFieldsMap]) => {
                if (authSystemFieldsMap[organizationId]?.[action.parentFieldId]?.[action.authSystemId]) {
                    return [];
                }
                return this._api.integrations.getAuthSystemFields(action.authSystemId).pipe(
                    map((response: any) => {
                        return actions.SetAuthSystemFields({
                            organizationId,
                            parentFieldId: action.parentFieldId,
                            authSystemId: action.authSystemId,
                            response,
                        });
                    }),
                    catchError(e => {
                        if (!e) {
                            return [];
                        }
                        return of(AlertError({ message: e }));
                    })
                );
            })
        )
    );

    public getConnectorRequiredFields$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.GetConnectorRequiredFields),
            withLatestFrom(this.store$.select(getCurrentOrganizationId$), this.store$.select(getConnectorRequiredFieldsMap$)),
            mergeMap(([action, organizationId, connectorRequiredFieldsMap]) => {
                if (connectorRequiredFieldsMap?.[action.resourceName]) {
                    return [];
                }
                return this._api.integrations.getConnectorRequiredFields(action.resourceName).pipe(
                    map((response: any) => {
                        return actions.SetConnectorRequiredFields({
                            organizationId,
                            resourceName: action.resourceName,
                            response: response?.response,
                        });
                    }),
                    catchError(e => {
                        if (!e) {
                            return [];
                        }
                        return of(AlertError({ message: e }));
                    })
                );
            })
        )
    );
}
